import React, { useState, useEffect } from 'react'
import {
  Key,
  Users,
  TrendingUp,
  AlertTriangle,
  Plus,
  Settings,
  Trash2,
} from 'lucide-react'
import { AdminHeader } from '../../../shared/components'
import { adminApiService } from '../../../shared/services/adminApi'
import { adminAuthService } from '../../../shared/services/adminAuth'
import toast from 'react-hot-toast'
import CreateApiKeyModal from '../components/apikey/CreateApiKeyModal'
import DeleteApiKeyModal from '../components/apikey/DeleteApiKeyModal'
import EditApiKeyModal from '../components/apikey/EditApiKeyModal'
import { adminApiKeyApi } from '../../../shared/services/adminApiKeyApi'

interface ApiKey {
  keyId: string
  name: string
  userId: {
    _id: string
    email: string
    name: string
    plan: string
  }
  permissions: string[]
  isActive: boolean
  lastUsed?: string
  expiresAt?: string
  createdAt: string
}

interface ApiUsage {
  keyId: string
  period: {
    start: string
    end: string
  }
  usage: Array<{
    totalRequests: number
    successfulRequests: number
    errorRequests: number
    _id: {
      year: number
      month: number
      day: number
    }
  }>
}

interface User {
  _id: string
  email: string
  name: string
  plan: string
}

interface CreateApiKeyData {
  userId: string
  name: string
  permissions: string[]
  expiresAt?: string
}

const ApiManagementPage: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [apiUsage, setApiUsage] = useState<Record<string, ApiUsage>>({})
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  // 모달 관련 상태
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null)

  // 편집 모달 상태
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingApiKey, setEditingApiKey] = useState<ApiKey | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  // 삭제 확인 모달 상태
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deletingApiKey, setDeletingApiKey] = useState<ApiKey | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // 폼 데이터
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [keyName, setKeyName] = useState('')
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([
    'url:create',
    'url:read',
  ])
  const [expirationDate, setExpirationDate] = useState('')

  // 편집 폼 데이터
  const [editKeyName, setEditKeyName] = useState('')
  const [editPermissions, setEditPermissions] = useState<string[]>([])
  const [editIsActive, setEditIsActive] = useState(true)

  // 사용자 검색
  const [userSearch, setUserSearch] = useState('')
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const availablePermissions = [
    { id: 'url:create', name: 'URL 생성', description: '단축 URL 생성 권한' },
    { id: 'url:read', name: 'URL 조회', description: '단축 URL 조회 권한' },
    { id: 'url:update', name: 'URL 수정', description: '단축 URL 수정 권한' },
    { id: 'url:delete', name: 'URL 삭제', description: '단축 URL 삭제 권한' },
    {
      id: 'analytics:read',
      name: '분석 조회',
      description: '클릭 분석 데이터 조회 권한',
    },
  ]

  useEffect(() => {
    fetchApiKeys()
  }, [])

  const fetchApiKeys = async () => {
    try {
      setLoading(true)

      const token = adminAuthService.getToken()
      if (!token) {
        toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.')
        return
      }

      const data = {
        success: true,
        data: { apiKeys: await adminApiKeyApi.list() },
      }
      {
        setApiKeys(data.data.apiKeys as ApiKey[])
        // 각 API 키의 사용량 조회
        data.data.apiKeys.forEach((key: ApiKey) => {
          fetchApiUsage(key.keyId)
        })
        setLastUpdated(new Date())
      }
    } catch (error: any) {
      console.error('API 키 목록 조회 실패:', error)
      toast.error('API 키 목록 조회에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true)
      await fetchApiKeys()
    } catch (error) {
      console.error('새로고침 실패:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const fetchApiUsage = async (keyId: string) => {
    try {
      const token = adminAuthService.getToken()
      if (!token) {
        return
      }

      const usage = await adminApiKeyApi.usage(keyId)
      {
        setApiUsage(prev => ({
          ...prev,
          [keyId]: usage as ApiUsage,
        }))
      }
    } catch (error) {
      console.error('API 사용량 조회 실패:', error)
    }
  }

  // 사용자 검색 함수
  const searchUsers = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    try {
      setIsSearching(true)
      const data = await adminApiService.searchUsers(query)
      setSearchResults(data.users)
    } catch (error) {
      console.error('사용자 검색 실패:', error)
      toast.error('사용자 검색에 실패했습니다')
    } finally {
      setIsSearching(false)
    }
  }

  // API 키 이름 자동 생성 함수
  const generateApiKeyName = (user: User, permissions?: string[]): string => {
    const userName = user.name || user.email.split('@')[0]
    const existingKeysCount = apiKeys.filter(
      key => key.userId._id === user._id
    ).length

    // 권한 기반 이름 생성
    if (permissions && permissions.length > 0) {
      const hasReadOnly = permissions.every(p => p.includes('read'))
      const hasFullAccess =
        permissions.includes('url:create') &&
        permissions.includes('url:update') &&
        permissions.includes('url:delete')

      if (hasReadOnly) {
        return `${userName} - Read-Only API Key`
      } else if (hasFullAccess) {
        return `${userName} - Full Access API Key`
      } else {
        return `${userName} - Custom API Key`
      }
    }

    // 기본 이름 생성
    if (existingKeysCount === 0) {
      return `${userName} - Production API Key`
    } else {
      return `${userName} - API Key #${existingKeysCount + 1}`
    }
  }

  // API 키 생성 함수
  const createApiKey = async () => {
    if (!selectedUser || !keyName.trim()) {
      toast.error('사용자와 키 이름을 입력해주세요')
      return
    }

    if (selectedPermissions.length === 0) {
      toast.error('최소 하나의 권한을 선택해주세요')
      return
    }

    try {
      setIsCreating(true)
      const createData: CreateApiKeyData = {
        userId: selectedUser._id,
        name: keyName.trim(),
        permissions: selectedPermissions,
      }

      if (expirationDate) {
        createData.expiresAt = expirationDate
      }

      const token = adminAuthService.getToken()
      if (!token) {
        toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.')
        return
      }

      const data = {
        success: true,
        data: await adminApiKeyApi.create(createData),
      }
      {
        setCreatedApiKey(data.data.apiKey)
        toast.success('API 키가 성공적으로 생성되었습니다')
        fetchApiKeys() // 목록 새로고침
      }
    } catch (error: any) {
      console.error('API 키 생성 실패:', error)
      toast.error('API 키 생성에 실패했습니다')
    } finally {
      setIsCreating(false)
    }
  }

  // 모달 초기화
  const resetModal = () => {
    setIsCreateModalOpen(false)
    setSelectedUser(null)
    setKeyName('')
    setSelectedPermissions(['url:create', 'url:read'])
    setExpirationDate('')
    setUserSearch('')
    setSearchResults([])
    setCreatedApiKey(null)
  }

  // API 키 복사
  const copyApiKey = async (apiKey: string) => {
    try {
      await navigator.clipboard.writeText(apiKey)
      toast.success('API 키가 클립보드에 복사되었습니다')
    } catch {
      toast.error('복사에 실패했습니다')
    }
  }

  // 편집 모달 열기
  const openEditModal = (apiKey: ApiKey) => {
    setEditingApiKey(apiKey)
    setEditKeyName(apiKey.name)
    setEditPermissions([...apiKey.permissions])
    setEditIsActive(apiKey.isActive)
    setIsEditModalOpen(true)
  }

  // API 키 수정
  const updateApiKey = async () => {
    if (!editingApiKey || !editKeyName.trim()) {
      toast.error('키 이름을 입력해주세요')
      return
    }

    if (editPermissions.length === 0) {
      toast.error('최소 하나의 권한을 선택해주세요')
      return
    }

    try {
      setIsUpdating(true)

      const token = adminAuthService.getToken()
      if (!token) {
        toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.')
        return
      }

      await adminApiKeyApi.update(editingApiKey.keyId, {
        name: editKeyName.trim(),
        permissions: editPermissions,
        isActive: editIsActive,
      })

      toast.success('API 키가 성공적으로 수정되었습니다')
      setIsEditModalOpen(false)
      fetchApiKeys() // 목록 새로고침
    } catch (error: any) {
      console.error('API 키 수정 실패:', error)
      toast.error('API 키 수정에 실패했습니다')
    } finally {
      setIsUpdating(false)
    }
  }

  // 삭제 확인 모달 열기
  const openDeleteModal = (apiKey: ApiKey) => {
    setDeletingApiKey(apiKey)
    setIsDeleteModalOpen(true)
  }

  // API 키 삭제
  const deleteApiKey = async () => {
    if (!deletingApiKey) {
      return
    }

    try {
      setIsDeleting(true)

      const token = adminAuthService.getToken()
      if (!token) {
        toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.')
        return
      }

      await adminApiKeyApi.remove(deletingApiKey.keyId)
      {
        toast.success('API 키가 성공적으로 삭제되었습니다')
        setIsDeleteModalOpen(false)
        fetchApiKeys() // 목록 새로고침
      }
    } catch (error: any) {
      console.error('API 키 삭제 실패:', error)
      toast.error('API 키 삭제에 실패했습니다')
    } finally {
      setIsDeleting(false)
    }
  }

  // 편집 모달 초기화
  const resetEditModal = () => {
    setIsEditModalOpen(false)
    setEditingApiKey(null)
    setEditKeyName('')
    setEditPermissions([])
    setEditIsActive(true)
  }

  // 삭제 모달 초기화
  const resetDeleteModal = () => {
    setIsDeleteModalOpen(false)
    setDeletingApiKey(null)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Admin Header */}
      <AdminHeader
        title="API 관리"
        subtitle="사용자 API 키 생성 및 관리 (Growth 플랜 이상 사용자만 API 접근 가능)"
        showRefresh={true}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastUpdated={lastUpdated}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="flex items-center text-xl font-semibold text-gray-900 dark:text-white">
              <Key className="mr-3" />
              API 키 관리
            </h2>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Growth 플랜 이상 사용자의 API 키를 관리합니다
            </p>
          </div>

          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            <Plus className="mr-2 h-4 w-4" />
            API 키 생성
          </button>
        </div>

        {/* 통계 카드 */}
        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-4">
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="flex items-center">
              <Key className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  총 API 키
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {apiKeys.length}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  활성 키
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {apiKeys.filter(key => key.isActive).length}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  총 요청 수
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Object.values(apiUsage).reduce(
                    (acc, usage) =>
                      acc +
                      (usage.usage?.reduce(
                        (dayAcc: number, day: any) =>
                          dayAcc + day.totalRequests,
                        0
                      ) || 0),
                    0
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  에러 요청
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Object.values(apiUsage).reduce(
                    (acc, usage) =>
                      acc +
                      (usage.usage?.reduce(
                        (dayAcc: number, day: any) =>
                          dayAcc + day.errorRequests,
                        0
                      ) || 0),
                    0
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* API 키 목록 */}
        <div className="rounded-lg bg-white shadow dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              API 키 목록
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                    키 정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                    사용자
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                    권한
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-300">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center">
                      <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600"></div>
                    </td>
                  </tr>
                ) : apiKeys.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      생성된 API 키가 없습니다.
                    </td>
                  </tr>
                ) : (
                  apiKeys.map(apiKey => (
                    <tr key={apiKey.keyId}>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {apiKey.name}
                          </div>
                          <div className="font-mono text-sm text-gray-500 dark:text-gray-400">
                            {apiKey.keyId}
                          </div>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {apiKey.userId.name || apiKey.userId.email}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {apiKey.userId.email}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {apiKey.permissions.map(permission => (
                            <span
                              key={permission}
                              className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                            >
                              {permission}
                            </span>
                          ))}
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            apiKey.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {apiKey.isActive ? '활성' : '비활성'}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            className="text-blue-600 hover:text-blue-900"
                            onClick={() => openEditModal(apiKey)}
                            title="설정"
                          >
                            <Settings className="h-4 w-4" />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => openDeleteModal(apiKey)}
                            title="삭제"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {isCreateModalOpen && (
          <CreateApiKeyModal
            createdApiKey={createdApiKey}
            keyName={keyName}
            onKeyNameChange={setKeyName}
            selectedPermissions={selectedPermissions}
            onPermissionsChange={setSelectedPermissions}
            expirationDate={expirationDate}
            onExpirationDateChange={setExpirationDate}
            availablePermissions={availablePermissions}
            userSearch={userSearch}
            onUserSearchChange={setUserSearch}
            searchResults={searchResults}
            onSearchResultsChange={setSearchResults}
            isSearching={isSearching}
            selectedUser={selectedUser}
            onSelectedUserChange={setSelectedUser}
            searchUsers={searchUsers}
            generateApiKeyName={generateApiKeyName}
            copyApiKey={copyApiKey}
            isCreating={isCreating}
            onCancel={resetModal}
            onSubmit={createApiKey}
          />
        )}

        {isEditModalOpen && (
          <EditApiKeyModal
            apiKey={editingApiKey}
            keyName={editKeyName}
            onKeyNameChange={setEditKeyName}
            permissions={editPermissions}
            onPermissionsChange={setEditPermissions}
            isActive={editIsActive}
            onIsActiveChange={setEditIsActive}
            availablePermissions={availablePermissions}
            isUpdating={isUpdating}
            onCancel={resetEditModal}
            onSubmit={updateApiKey}
          />
        )}

        {isDeleteModalOpen && (
          <DeleteApiKeyModal
            apiKey={deletingApiKey}
            isDeleting={isDeleting}
            onCancel={resetDeleteModal}
            onConfirm={deleteApiKey}
          />
        )}
      </div>
    </div>
  )
}

export default ApiManagementPage
